'use es6';

import { combineReducers } from 'redux';
import Actions from 'AdsUI/lib/Actions';
import { Map as ImmutableMap } from 'immutable';
import { AdWordsAdTypes, EntityTypes, Networks } from 'AdsUI/lib/Constants';
import AdWordsAdGroupRecord from 'AdsUI/records/reporting/AdWordsAdGroupRecord';
import AdWordsLegacyAdGroupRecord from 'AdsUI/records/reporting/AdWordsLegacyAdGroupRecord';
import FBAdSetRecord from 'AdsUI/records/reporting/FBAdSetRecord';
import FBAdRecord from 'AdsUI/records/reporting/FBAdRecord';
import ReportingCampaignRecord from 'AdsUI/records/reporting/ReportingCampaignRecord';
import LinkedInAdRecord from 'AdsUI/records/reporting/LinkedInAdRecord';
import LinkedInAdGroupRecord from 'AdsUI/records/reporting/LinkedInAdGroupRecord';
import AdWordsKeywordReportingEntityRecord from 'AdsUI/records/reporting/AdWordsKeywordReportingEntityRecord';
import CampaignFilterRecord from 'AdsUI/records/reporting/CampaignFilterRecord';
import ReportRecord from 'AdsUI/records/reporting/ReportRecord';
import { makeOptimisticUpdate, rollbackOptimisticUpdate, finishOptimisticUpdate } from 'AdsUI/lib/ReducerHelpers';
import TrackingStatusParser from 'AdsUI/lib/TrackingStatusParser';
import ChildrenReportRecord from 'AdsUI/records/reporting/ChildrenReportRecord';
import Raven from 'raven-js';
import AdWordsRecordBuilder from 'AdsUI/lib/AdWordsRecordBuilder';
import AdWordsGenericAdReportingRecord from 'AdsUI/records/reporting/AdWordsGenericAdReportingRecord';
import AdWordsTextAdReportingRecord from 'AdsUI/records/reporting/AdWordsTextAdReportingRecord';
import AdWordsImageAdReportingRecord from 'AdsUI/records/reporting/AdWordsImageAdReportingRecord';
import AdWordsLegacyAppAdReportingRecord from 'AdsUI/records/reporting/AdWordsLegacyAppAdReportingRecord';
import SequenceDetailsRecord from '../records/reporting/SequenceDetailsRecord';
import SequenceEntityRecord from '../records/reporting/SequenceEntityRecord';
import ReportingDataRecord from '../records/reporting/ReportingDataRecord';
import { convertTimeRangeToKey } from 'AdsUI/lib/Utils';
const AD_TYPE_RECORD_TYPE = {
  [AdWordsAdTypes.TEXT_AD]: AdWordsTextAdReportingRecord,
  [AdWordsAdTypes.RESPONSIVE_SEARCH_AD]: AdWordsTextAdReportingRecord,
  [AdWordsAdTypes.SMART_CAMPAIGN_AD]: AdWordsTextAdReportingRecord,
  [AdWordsAdTypes.IMAGE_AD]: AdWordsImageAdReportingRecord,
  [AdWordsAdTypes.LEGACY_APP_INSTALL_AD]: AdWordsLegacyAppAdReportingRecord
};

const buildAdWordsAdReportingRecord = adProps => {
  const AdRecord = AD_TYPE_RECORD_TYPE[adProps.type] || AdWordsGenericAdReportingRecord;
  return AdRecord(adProps);
};

const EntityRecordTypes = {
  [Networks.ADWORDS]: {
    // TODO: Can be removed once ungated to Google Ads API
    [EntityTypes.ADGROUP]: adGroupProps => adGroupProps.biddingStrategyConfiguration ? AdWordsLegacyAdGroupRecord(adGroupProps) : AdWordsAdGroupRecord(adGroupProps),
    [EntityTypes.AD]: adProps => adProps.adType ? AdWordsRecordBuilder.AdWordsAdEntityRecord(adProps) : buildAdWordsAdReportingRecord(adProps),
    [EntityTypes.KEYWORD]: props => new AdWordsKeywordReportingEntityRecord(props)
  },
  [Networks.FACEBOOK]: {
    [EntityTypes.ADGROUP]: props => new FBAdSetRecord(props),
    [EntityTypes.AD]: props => new FBAdRecord(props)
  },
  [Networks.LINKEDIN]: {
    [EntityTypes.ADGROUP]: props => new LinkedInAdGroupRecord(props),
    [EntityTypes.AD]: props => new LinkedInAdRecord(props)
  }
};

const campaignEntities = (state = ImmutableMap(), action) => {
  const {
    type,
    payload
  } = action;

  if (!payload) {
    return state;
  }

  switch (type) {
    case Actions.CAMPAIGN_DETAILS_FETCH_SUCCEEDED:
      {
        const {
          entity,
          filters: {
            entityId,
            network
          }
        } = payload;
        const campaignFilter = CampaignFilterRecord({
          entityId,
          network
        });
        const campaignData = new ReportingCampaignRecord(entity);
        return state.set(campaignFilter, campaignData);
      }

    case Actions.CAMPAIGN_REPORTING_ENTITY_UPDATE_START:
      {
        const {
          update,
          entityId,
          network,
          entityType
        } = payload;
        const campaignFilter = CampaignFilterRecord({
          entityId,
          network
        });

        if (entityType !== EntityTypes.CAMPAIGN || !state.has(campaignFilter)) {
          return state;
        }

        return makeOptimisticUpdate(state, campaignFilter, update);
      }

    case Actions.CAMPAIGN_REPORTING_ENTITY_UPDATE_FAILED:
      {
        const {
          entityId,
          network,
          entityType
        } = payload;
        const campaignFilter = CampaignFilterRecord({
          entityId,
          network
        });

        if (entityType !== EntityTypes.CAMPAIGN || !state.has(campaignFilter)) {
          return state;
        }

        return rollbackOptimisticUpdate(state, campaignFilter);
      }

    case Actions.CAMPAIGN_REPORTING_ENTITY_UPDATE_SUCCEEDED:
      {
        const {
          entityType,
          entityId,
          network
        } = payload;
        const campaignFilter = CampaignFilterRecord({
          entityId,
          network
        });

        if (entityType !== EntityTypes.CAMPAIGN || !state.has(campaignFilter)) {
          return state;
        }

        return finishOptimisticUpdate(state, campaignFilter);
      }

    default:
      return state;
  }
};

const campaignReports = (state = ImmutableMap(), action) => {
  const {
    type,
    payload
  } = action;

  if (!payload) {
    return state;
  }

  switch (type) {
    case Actions.CAMPAIGN_DETAILS_FETCH_SUCCEEDED:
      {
        const {
          report,
          filters: {
            entityId,
            network,
            timeRange,
            attributionView
          }
        } = payload;
        const campaignFilter = CampaignFilterRecord({
          entityId,
          network,
          timeRange,
          attributionView
        });
        const campaignReport = new ReportRecord(report);
        return state.set(campaignFilter, campaignReport);
      }

    default:
      return state;
  }
};

const childEntities = (state = ImmutableMap(), action) => {
  const {
    type,
    payload
  } = action;

  switch (type) {
    case Actions.CHILDREN_BY_ENTITY_FETCH_SUCCEEDED:
      {
        if (!payload) {
          return state;
        }

        const {
          filters: {
            network,
            entityId,
            entityType,
            statuses
          },
          response: {
            reports
          }
        } = payload;

        if (!reports) {
          Raven.captureMessage('No reports detected', {
            extra: {
              action
            }
          });
          return state;
        }

        const campaignFilter = CampaignFilterRecord({
          network,
          entityId,
          entityType,
          statuses
        });
        const campaignList = reports.reduce((childMap, child) => childMap.set(child.entity.id, EntityRecordTypes[network][entityType](child.entity)), ImmutableMap());
        return state.mergeIn([campaignFilter], campaignList);
      }

    case Actions.CAMPAIGN_REPORTING_ENTITY_UPDATE_START:
      {
        const {
          update,
          entityId,
          network,
          entityType,
          parent
        } = payload;

        if (entityType === EntityTypes.CAMPAIGN) {
          return state;
        }

        return state.reduce((newState, entityMap, entityFilter) => {
          if (entityFilter.network === network && entityFilter.entityId === parent.id && entityFilter.entityType === entityType) {
            return newState.set(entityFilter, makeOptimisticUpdate(entityMap, entityId, update));
          }

          return newState.set(entityFilter, entityMap);
        }, ImmutableMap());
      }

    case Actions.CAMPAIGN_REPORTING_ENTITY_UPDATE_FAILED:
      {
        const {
          network,
          entityType,
          entityId,
          parent
        } = payload;

        if (entityType === EntityTypes.CAMPAIGN) {
          return state;
        }

        return state.reduce((newState, entityMap, entityFilter) => {
          if (entityFilter.network === network && entityFilter.entityId === parent.id && entityFilter.entityType === entityType) {
            return newState.set(entityFilter, rollbackOptimisticUpdate(entityMap, entityId));
          }

          return newState.set(entityFilter, entityMap);
        }, ImmutableMap());
      }

    case Actions.CAMPAIGN_REPORTING_ENTITY_UPDATE_SUCCEEDED:
      {
        if (!payload) {
          return state;
        }

        const {
          entityType,
          network,
          entityId,
          parent
        } = payload;

        if (entityType === EntityTypes.CAMPAIGN) {
          return state;
        }

        return state.reduce((newState, entityMap, entityFilter) => {
          if (entityFilter.network === network && entityFilter.entityId === parent.id && entityFilter.entityType === entityType) {
            return newState.set(entityFilter, finishOptimisticUpdate(entityMap, entityId));
          }

          return newState.set(entityFilter, entityMap);
        }, ImmutableMap());
      }
    // The network-specific modals

    case Actions.ENTITY_TRACKING_STATUS_INVALIDATE:
    case Actions.ADWORDS_ADGROUP_UPDATE_SUCCEEDED:
    case Actions.ADWORDS_ADGROUP_DELETE_SUCCEEDED:
    case Actions.ADWORDS_ADGROUP_CREATE_SUCCEEDED:
    case Actions.ADWORDS_AD_UPDATE_SUCCEEDED:
    case Actions.ADWORDS_AD_CREATE_SUCCEEDED:
    case Actions.KEYWORD_UPDATE_SUCCEEDED:
    case Actions.KEYWORD_CREATE_SUCCEEDED:
    case Actions.KEYWORD_BULK_CREATE_SUCCEEDED:
    case Actions.ENTITY_DELETE_SUCCEEDED:
    case Actions.CHILD_ENTITY_FETCH_RESET:
      {
        // This will force a reload in CampaignDetailsTableContainer. By the time we reload,
        // we should get the updated information.
        return ImmutableMap();
      }

    default:
      return state;
  }
};

const childReports = (state = ImmutableMap(), action) => {
  const {
    type,
    payload
  } = action;

  switch (type) {
    case Actions.CHILDREN_BY_ENTITY_FETCH_SUCCEEDED:
      {
        if (!payload) {
          return state;
        }

        const {
          filters,
          response: {
            reports,
            totals
          }
        } = payload;
        const campaignFilter = CampaignFilterRecord(filters);
        const reportMap = reports.reduce((childMap, child) => childMap.set(child.entity.id, new ReportRecord(child.report)), ImmutableMap());
        return state.set(campaignFilter, new ChildrenReportRecord({
          reportMap,
          totals: totals && new ReportRecord(totals)
        }));
      }
    // The network-specific modals

    case Actions.ENTITY_TRACKING_STATUS_INVALIDATE:
    case Actions.ADWORDS_ADGROUP_UPDATE_SUCCEEDED:
    case Actions.ADWORDS_ADGROUP_DELETE_SUCCEEDED:
    case Actions.ADWORDS_ADGROUP_CREATE_SUCCEEDED:
    case Actions.ADWORDS_AD_UPDATE_SUCCEEDED:
    case Actions.ADWORDS_AD_CREATE_SUCCEEDED:
    case Actions.KEYWORD_UPDATE_SUCCEEDED:
    case Actions.KEYWORD_CREATE_SUCCEEDED:
    case Actions.KEYWORD_BULK_CREATE_SUCCEEDED:
    case Actions.ENTITY_DELETE_SUCCEEDED:
    case Actions.CHILD_ENTITY_FETCH_RESET:
      {
        // This will force a reload in CampaignDetailsTableContainer. By the time we reload,
        // we should get the updated information.
        return ImmutableMap();
      }

    default:
      return state;
  }
};

const trackingStatuses = (state = ImmutableMap(), action) => {
  const {
    type,
    payload
  } = action;

  switch (type) {
    case Actions.CAMPAIGN_DETAILS_FETCH_SUCCEEDED:
      {
        if (!payload) {
          return state;
        }

        const {
          trackingStatus,
          filters: {
            entityId,
            network
          }
        } = payload;
        const campaignFilter = CampaignFilterRecord({
          entityId,
          network
        });
        return state.set(campaignFilter, TrackingStatusParser.parse(trackingStatus));
      }

    case Actions.CHILDREN_BY_ENTITY_FETCH_SUCCEEDED:
      {
        if (!payload) {
          return state;
        }

        const {
          filters: {
            network,
            entityType
          },
          response: {
            reports
          }
        } = payload;
        return reports.reduce((newState, child) => newState.set(CampaignFilterRecord({
          entityId: child.entity.id,
          entityType,
          network
        }), TrackingStatusParser.parse(child.trackingStatus)), state);
      }

    case Actions.ENTITY_TRACKING_STATUS_INVALIDATE:
    case Actions.CHILD_ENTITY_FETCH_RESET:
      return ImmutableMap();

    default:
      return state;
  }
};

const sequenceCampaignToRecord = campaign => new ReportingDataRecord({
  entity: new ReportingCampaignRecord(campaign.entity),
  report: new ReportRecord(campaign.report),
  trackingStatus: TrackingStatusParser.parse(campaign.trackingStatus)
});

const sequences = (state = ImmutableMap(), action) => {
  if (!action.payload) {
    return state;
  }

  switch (action.type) {
    case Actions.SEQUENCE_DETAILS_FETCH_SUCCEEDED:
      {
        const {
          filters: {
            timeRange,
            sequenceId,
            attributionView
          },
          entity: {
            createdAdSequence
          },
          report,
          trackingStatus,
          attractStageCampaign,
          convertStageCampaign,
          closeStageCampaign,
          stageAutomationStatusByCampaignId
        } = action.payload;
        return state.setIn([sequenceId, convertTimeRangeToKey(timeRange), attributionView], new SequenceDetailsRecord({
          entity: new SequenceEntityRecord(createdAdSequence),
          report: new ReportRecord(report),
          trackingStatus: TrackingStatusParser.parse(trackingStatus),
          attractStageCampaign: sequenceCampaignToRecord(attractStageCampaign),
          convertStageCampaign: sequenceCampaignToRecord(convertStageCampaign),
          closeStageCampaign: sequenceCampaignToRecord(closeStageCampaign),
          stageAutomationStatusByCampaignId
        }));
      }

    case Actions.SEQUENCE_CAMPAIGN_STATUS_UPDATE_START:
      {
        const {
          update: {
            status
          },
          sequenceId,
          sequenceStep,
          globalFilters: {
            timeRange,
            attributionView
          }
        } = action.payload;
        const previousState = state.getIn([sequenceId, convertTimeRangeToKey(timeRange), attributionView, `${sequenceStep}StageCampaign`, 'entity']);
        return state.mergeIn([sequenceId, convertTimeRangeToKey(timeRange), attributionView, `${sequenceStep}StageCampaign`, 'entity'], {
          status,
          previousState
        });
      }

    case Actions.SEQUENCE_CAMPAIGN_STATUS_UPDATE_FAILED:
      {
        const {
          sequenceId,
          sequenceStep,
          globalFilters: {
            timeRange,
            attributionView
          }
        } = action.payload;
        const previousState = state.getIn([sequenceId, convertTimeRangeToKey(timeRange), attributionView, `${sequenceStep}StageCampaign`, 'entity', 'previousState']);
        return state.mergeIn([sequenceId, convertTimeRangeToKey(timeRange), attributionView, `${sequenceStep}StageCampaign`, 'entity'], {
          previousState
        });
      }

    case Actions.SEQUENCE_CAMPAIGN_STATUS_UPDATE_SUCCEEDED:
      {
        const {
          sequenceId,
          sequenceStep,
          globalFilters: {
            timeRange,
            attributionView
          }
        } = action.payload;
        return state.mergeIn([sequenceId, convertTimeRangeToKey(timeRange), attributionView, `${sequenceStep}StageCampaign`, 'entity'], {
          previousState: null
        });
      }

    default:
      {
        return state;
      }
  }
};

export default combineReducers({
  campaignEntities,
  campaignReports,
  childEntities,
  childReports,
  trackingStatuses,
  sequences
});